// InfluencerPreview step - Review and confirm profile before final submission
// This is Step 4 of the influencer onboarding wizard
// Displays a preview of the complete profile and handles final Firestore write

import React, { useState } from 'react';
import { useAuth } from '../../../../context/AuthContext.jsx';
import { createUserWithEmailAndPassword, updateProfile, fetchSignInMethodsForEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../../../../firebaseConfig.js';
import { reserveUniqueHandle, finalizeHandle, cleanupPendingHandle } from '../../../../services/handles.js';
import ProfilePreviewCard from '../../../../components/ProfilePreviewCard.jsx';
import { validatePassword } from '../../../../utils/passwordValidation.js';
import { normalizePhone } from '../../../../utils/phoneValidation.js';
import { uploadUserImage } from '../../../../lib/uploadProfileImage.js';
import { incrementCreatorCount } from '../../../../utils/incrementCreatorCount.js';
import { triggerWelcomeEmail } from '../../../../lib/email/triggerWelcomeEmail';

const InfluencerPreview = ({ data, onBack, onNavigate }) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Extract data from wizard state
  const accountData = data.account || {};
  const audienceData = data.audience || {};
  const bioData = data.bio || {};
  const mediaData = data.media || {}; // Profile picture is stored here from InfluencerAudience step
  const socialLinks = data.socialLinks || {};

  // Check if at least one social media profile is provided
  const socialFields = ['instagram', 'tiktok', 'youtube', 'x', 'website'];
  const hasAtLeastOneSocial = socialFields.some(field => {
    const value = socialLinks[field];
    return value && value.trim().length > 0;
  });

  // Check if all required fields are present
  // Debug: Log the data to help diagnose issues
  console.log('🔍 Preview validation - data structure:', {
    account: accountData,
    audience: audienceData,
    bio: bioData,
    socialLinks: socialLinks,
    fullData: data
  });
  
  const isComplete = accountData.email && 
                    accountData.firstName && 
                    accountData.lastName && 
                    accountData.phone && 
                    audienceData.totalFollowers && 
                    audienceData.niches?.length > 0 && 
                    audienceData.engagementRate &&
                    bioData.text &&
                    hasAtLeastOneSocial;
  
  console.log('🔍 Preview validation - isComplete:', isComplete, {
    hasEmail: !!accountData.email,
    hasFirstName: !!accountData.firstName,
    hasLastName: !!accountData.lastName,
    hasPhone: !!accountData.phone,
    hasTotalFollowers: !!audienceData.totalFollowers,
    hasNiches: audienceData.niches?.length > 0,
    hasEngagementRate: !!audienceData.engagementRate,
    hasBio: !!bioData.text,
    hasSocial: hasAtLeastOneSocial
  });


  const handleConfirm = async () => {
    // Prevent double submission
    if (isSubmitting) {
      console.log('⚠️ Submission already in progress, ignoring duplicate click');
      return;
    }
    
    console.log('🚀 Starting influencer profile creation...');
    
    if (!isComplete) {
      console.log('❌ Form not complete, cannot submit');
      
      // Provide specific error message for missing social media
      if (!hasAtLeastOneSocial) {
        setError('Please add at least one social media profile so brands can review your content.');
      } else {
        setError('Please complete all required fields before confirming.');
      }
      return;
    }

    console.log('✅ Form complete, starting submission...');
    setIsSubmitting(true);
    setError('');

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.error('⏰ Profile creation timed out after 30 seconds');
      setError('Profile creation is taking longer than expected. Please try again.');
      setIsSubmitting(false);
    }, 30000);

    let reservedHandle = null;

    try {
      // Step 1: Check if user already exists and has a complete profile
      console.log('📧 Step 1: Checking if account already exists...');
      const signInMethods = await fetchSignInMethodsForEmail(auth, accountData.email);
      
      if (signInMethods.length > 0) {
        // Auth user exists - try to sign in and check Firestore profile
        console.log('⚠️ Auth user exists, checking Firestore profile...');
        try {
          const signInCredential = await signInWithEmailAndPassword(
            auth,
            accountData.email,
            accountData.password
          );
          const existingUser = signInCredential.user;
          
          // Check if Firestore profile exists
          const userDocRef = doc(db, 'users', existingUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            // Profile exists - redirect to Profile Builder (approval no longer required)
            console.log('✅ Profile already exists, redirecting to Profile Builder...');
            clearTimeout(timeoutId);
            setIsSubmitting(false);
            onNavigate('profile-edit');
            return;
          } else {
            // Auth user exists but no Firestore profile - complete the profile creation
            console.log('⚠️ Auth user exists but no Firestore profile - completing profile creation...');
            // Continue with profile creation using existing auth user
            const currentUser = existingUser;
            
            // Skip to Step 4.5 (handle reservation) and continue
            const handleBase = `${accountData.firstName}${accountData.lastName}`;
            reservedHandle = await reserveUniqueHandle(db, handleBase);
            console.log('✅ Handle reserved:', reservedHandle);
            
            // Upload profile picture if provided
            let photoUrl = null;
            if (mediaData.profilePictureFile) {
              console.log('📸 Uploading profile picture...');
              try {
                photoUrl = await uploadUserImage(mediaData.profilePictureFile, currentUser.uid, 'profile');
                console.log('✅ Profile picture uploaded:', photoUrl);
              } catch (uploadError) {
                console.error('❌ Error uploading profile picture:', uploadError);
              }
            }
            
            // Build profile data
            const profileData = {
              account: {
                phone: normalizePhone(accountData.phone)
              },
              media: {
                profilePictureUrl: photoUrl || null
              },
              audience: {
                totalFollowers: audienceData.totalFollowers,
                niches: audienceData.niches || [],
                engagementRate: audienceData.engagementRate,
                analyticsScreenshotUrl: audienceData.analyticsScreenshotUrl || null
              },
              bio: {
                text: bioData.text
              },
              socialLinks: {}
            };
            
            if (data.socialLinks) {
              Object.keys(data.socialLinks).forEach(key => {
                if (data.socialLinks[key] && data.socialLinks[key].trim()) {
                  profileData.socialLinks[key] = data.socialLinks[key].trim();
                }
              });
            }
            
            const displayName = `${accountData.firstName} ${accountData.lastName}`;
            
            // Create Firestore profile
            // NOTE: Approval workflow removed – new influencers are treated as approved by default.
            await setDoc(userDocRef, {
              uid: currentUser.uid,
              email: accountData.email,
              firstName: accountData.firstName,
              lastName: accountData.lastName,
              displayName: displayName,
              handle: reservedHandle,
              userType: 'influencer',
              status: 'approved', // Open signup - influencers are approved immediately
              submittedAt: serverTimestamp(),
              photoUrl: photoUrl || null,
              profile: profileData,
              createdAt: serverTimestamp()
            });
            
            // Increment creator count (this is a new creator)
            await incrementCreatorCount();
            
            await finalizeHandle(db, reservedHandle, currentUser.uid);
            localStorage.removeItem(`ih.wizardDraft.${currentUser.uid}`);
            
            clearTimeout(timeoutId);
            console.log('✅ Profile created successfully, redirecting to Profile Builder...');
            setTimeout(() => {
              onNavigate('profile-edit');
            }, 500);
            return;
          }
        } catch (signInError) {
          if (signInError.code === 'auth/wrong-password') {
            clearTimeout(timeoutId);
            setIsSubmitting(false);
            setError(
              <>
                That email already has an account with a different password. 
                <button
                  type="button"
                  onClick={() => onNavigate('login')}
                  className="text-blue-600 hover:text-blue-500 underline ml-1"
                >
                  Sign in instead
                </button>
              </>
            );
            return;
          }
          // If sign in fails for other reasons, continue with new account creation
          console.log('⚠️ Could not sign in to existing account, continuing with new account creation...');
        }
      }

      // Step 2: Generate and reserve unique handle
      // NOTE: This happens BEFORE creating the Auth user, so we need to handle this differently
      // For now, we'll create the Auth user first, then reserve the handle
      // Actually, let's move handle reservation to after Auth user creation
      console.log('🏷️ Step 2: Skipping handle reservation until after Auth user creation...');
      const handleBase = `${accountData.firstName}${accountData.lastName}`;

      // Step 3: Server-side password validation (prevent bypassing client validation)
      if (!accountData.password) {
        throw new Error('Password is required');
      }
      const passwordValidation = validatePassword(accountData.password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.errors.join('. '));
      }

      // Step 3: Create Firebase Auth user
      console.log('👤 Step 3: Creating Firebase Auth user...');
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        accountData.email, 
        accountData.password
      );
      const currentUser = userCredential.user;
      console.log('✅ Firebase user created:', currentUser.uid);

      // Step 4: Update user profile with displayName
      const displayName = `${accountData.firstName} ${accountData.lastName}`;
      await updateProfile(currentUser, {
        displayName: displayName
      });
      
      // Step 4.5: Now reserve handle (AFTER Auth user is created, so we're authenticated)
      console.log('🏷️ Step 4.5: Generating and reserving unique handle (after Auth)...');
      reservedHandle = await reserveUniqueHandle(db, handleBase);
      console.log('✅ Handle reserved:', reservedHandle);

      // Step 5: Upload profile picture if provided
      let photoUrl = null;
      if (mediaData.profilePictureFile) {
        console.log('📸 Step 5: Uploading profile picture...');
        try {
          photoUrl = await uploadUserImage(mediaData.profilePictureFile, currentUser.uid, 'profile');
          console.log('✅ Profile picture uploaded:', photoUrl);
        } catch (uploadError) {
          console.error('❌ Error uploading profile picture:', uploadError);
          // Don't block signup if upload fails - just log the error
          // User can upload later from Settings
        }
      }

      // Step 6: Build the final profile object
      const profileData = {
        account: {
          phone: normalizePhone(accountData.phone) // Store normalized phone (digits only)
        },
        media: {
          profilePictureUrl: photoUrl || null  // Use uploaded photoUrl if available
        },
        audience: {
          totalFollowers: audienceData.totalFollowers,
          niches: audienceData.niches || [],
          engagementRate: audienceData.engagementRate,
          analyticsScreenshotUrl: audienceData.analyticsScreenshotUrl || null
        },
        bio: {
          text: bioData.text
        },
        socialLinks: {}
      };

      // Only include social links that have values
      if (data.socialLinks) {
        Object.keys(data.socialLinks).forEach(key => {
          if (data.socialLinks[key] && data.socialLinks[key].trim()) {
            profileData.socialLinks[key] = data.socialLinks[key].trim();
          }
        });
      }


      // Step 7: Create base user document and complete profile in one operation
      console.log('💾 Step 7: Creating user document in Firestore...');
      console.log('💾 Current user UID:', currentUser.uid);
      console.log('💾 Auth state:', auth.currentUser?.uid);
      console.log('💾 Profile picture URL being saved:', photoUrl);
      console.log('💾 Full profileData structure:', JSON.stringify(profileData, null, 2));
      
      // Ensure user is authenticated before writing
      if (!auth.currentUser || auth.currentUser.uid !== currentUser.uid) {
        throw new Error('User authentication not properly established. Please try again.');
      }
      
      // Use setDoc without merge for initial create (document doesn't exist yet)
      // NOTE: Approval workflow removed – new influencers are treated as approved by default.
      await setDoc(doc(db, 'users', currentUser.uid), {
        uid: currentUser.uid,
        email: accountData.email,
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        displayName: displayName,
        handle: reservedHandle,
        userType: 'influencer',
        status: 'approved', // Open signup - influencers are approved immediately
        submittedAt: serverTimestamp(),
        photoUrl: photoUrl || null, // Save at root level for easy access
        profile: profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        emailSentAt: {},
        featureFirstUse: {},
        emailMeta: {},
      });
      
      // Increment creator count (this is a new creator)
      await incrementCreatorCount();

      console.log('[signup] Firestore user doc created (influencer wizard)', { uid: currentUser.uid });
      await triggerWelcomeEmail(currentUser.uid);

      console.log('✅ User document created successfully');
      console.log('✅ Profile picture URL saved to photoUrl:', photoUrl);
      console.log('✅ Profile picture URL saved to profile.media.profilePictureUrl:', profileData.media.profilePictureUrl);
      
      // Verify the data was saved correctly
      const verifyRef = doc(db, 'users', currentUser.uid);
      const verifySnap = await getDoc(verifyRef);
      if (verifySnap.exists()) {
        const savedData = verifySnap.data();
        console.log('🔍 Verification - Saved profile.media.profilePictureUrl:', savedData?.profile?.media?.profilePictureUrl);
      }

      // Step 8: Finalize handle reservation
      await finalizeHandle(db, reservedHandle, currentUser.uid);

      // Step 9: Clear localStorage draft
      localStorage.removeItem(`ih.wizardDraft.${currentUser.uid}`);
      localStorage.removeItem('ih.wizardDraft.preAuth');

      // Step 10: Navigate to Profile Builder (approval no longer required)
      console.log('✅ Profile created successfully, navigating to Profile Builder...');
      clearTimeout(timeoutId);
      setIsSubmitting(false);
      
      // Small delay to ensure Firestore document is fully committed
      setTimeout(() => {
        onNavigate('profile-edit');
      }, 500);

    } catch (error) {
      console.error('❌ Error confirming profile:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Clean up reserved handle if user creation failed
      if (reservedHandle) {
        try {
          await cleanupPendingHandle(db, reservedHandle);
          console.log('✅ Cleaned up reserved handle');
        } catch (cleanupError) {
          console.error('❌ Error cleaning up handle:', cleanupError);
        }
      }
      
      // Check if Auth user was created but Firestore write failed
      // If so, try to complete the profile creation on next attempt
      if (auth.currentUser && error.code !== 'auth/email-already-in-use') {
        console.log('⚠️ Auth user exists but profile creation failed - user can retry to complete profile');
      }
      
      let errorMessage = 'Failed to create profile. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        // This should now be handled by the recovery logic above, but keep as fallback
        errorMessage = 'That email already has an account. If you just created it, try submitting again to complete your profile.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.message.includes('Handle')) {
        errorMessage = 'Unable to generate unique handle. Please try again.';
      } else if (error.message.includes('Firebase') || error.code?.includes('permission')) {
        errorMessage = `Database error: ${error.message}. Please check your connection and Firestore rules.`;
      } else if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        errorMessage = 'Permission denied. Please ensure you are properly authenticated and Firestore rules allow user creation.';
      } else {
        errorMessage = `Failed to create profile: ${error.message}. If you just created an account, try submitting again.`;
      }
      
      setError(errorMessage);
      console.error('❌ Set error message:', errorMessage);
    } finally {
      console.log('🔄 Setting isSubmitting to false');
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 pt-6 md:pt-8">
      {/* Profile Preview Card */}
      <ProfilePreviewCard
        avatarUrl={mediaData.profilePictureFile ? URL.createObjectURL(mediaData.profilePictureFile) : (data.media?.profilePictureUrl || null)}
        displayName={`${accountData.firstName} ${accountData.lastName}`}
        handle={null} // Will be generated on confirm
        location={accountData.location || ''}
        bio={bioData.text}
        niches={audienceData.niches}
        totalFollowers={audienceData.totalFollowers}
        engagementRate={audienceData.engagementRate}
        platformBreakdown={[]} // Could be enhanced later with platform-specific data
        socialLinks={data.socialLinks || {}}
      />

      {/* Error Message */}
      {error && (
        <div className="mt-6 max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Buttons Row - Centered below card */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        {/* Back to Edit Button */}
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          aria-label="Go back to edit profile"
        >
          Back to Edit
        </button>
        
        {/* Submit for Approval Button */}
        {!isComplete ? (
          <button
            type="button"
            disabled
            className="inline-flex items-center justify-center rounded-lg bg-gray-300 px-6 py-2.5 text-gray-500 font-medium cursor-not-allowed"
            aria-label="Complete required fields to continue"
          >
            Complete Required Fields
          </button>
        ) : (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-2.5 text-white font-medium shadow-md hover:shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            aria-label="Submit for approval"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Profile...
              </div>
            ) : (
              'Submit for Approval'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default InfluencerPreview;
